import { BaseComponent, BaseComponentProps, BaseProps } from '../types';

export interface CreateElementOptions extends BaseProps {
  alt?: string;
  tagName: string;
  innerHtml?: string;
}

export const createElement = <T extends HTMLElement>({
  tagName,
  className,
  dataHook,
  id,
  alt,
  attributes,
  innerHtml,
  innerText,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  role,
}: CreateElementOptions): T => {
  let element = document.createElement(tagName) as T;

  if (innerHtml) {
    const div = document.createElement('div');
    div.innerHTML = innerHtml;
    element = div.firstElementChild as T;
  }

  if (className) {
    element.className = className;
  }
  if (id) {
    element.id = id;
  }
  if (attributes) {
    Object.keys(attributes).forEach((attr) => {
      element.setAttribute(attr, attributes[attr]);
    });
  }

  if (innerText) {
    element.innerText = innerText;
  }
  if (dataHook) {
    element.setAttribute('data-hook', dataHook);
  }
  if (ariaLabel) {
    element.setAttribute('aria-label', ariaLabel);
  }
  if (ariaLabelledBy) {
    element.setAttribute('aria-labelledby', ariaLabelledBy);
  }
  if (ariaDescribedBy) {
    element.setAttribute('aria-describedby', ariaDescribedBy);
  }
  if (alt) {
    element.setAttribute('alt', alt);
  }
  if (role) {
    element.setAttribute('role', role);
  }

  return element as T;
};

export const classNames = (classesObject: any): string => {
  let className: string = '';
  Object.keys(classesObject).forEach((clz) => {
    if (classesObject[clz]) {
      className = !className ? clz : `${className} ${clz}`;
    }
  });
  return className;
};

export function renderComponent<T extends BaseComponentProps, S>(
  component: BaseComponent<T, S>,
  target: HTMLElement = document.body,
): Promise<void> {
  component.htmlElement = component.render();
  target.appendChild(component.htmlElement);

  if (!component.rendered) {
    return new Promise((resolve) => setTimeout(resolve, 10)).then(() => {
      component.componentRendered();
      component.rendered = true;
    });
  }
  component.onComponentUpdate();
  return Promise.resolve();
}
